import { cloneDeep } from "lodash";
import React, { FC, useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useDataLayer } from "../../../hooks/use-data-layer";
import { FilterContentGroup, GoogleAnalyticsEvent } from "../../../utils/analytics";
import { Container } from "../../container";
import { EventsFilters, EventsFiltersSummary } from "../../events/events-filters";
import { EventsGrid } from "../../events/events-grid";
import { Event, EventsFiltersForm, EventsLabels } from "../../events/types";
import { FORM_DEFAULT_VALUES } from "./form-default-values";
import { useEventsFilters } from "./use-events-filters";
import { useFilteredEvents } from "./use-filtered-events";

export interface EventsGridInnerProps {
  events: Event[];
  loading: boolean;
  error: boolean;
  labels: EventsLabels;
  eventDetailPagePath: string | null;
}

export const EventsGridInner: FC<EventsGridInnerProps> = ({
  labels,
  events,
  error,
  loading,
  eventDetailPagePath,
}) => {
  const { pushEvent } = useDataLayer();
  const { appliedFilters, setAppliedFilters } = useEventsFilters();
  const { filteredEvents, timeIntervalEvents } = useFilteredEvents(events, appliedFilters);

  const form = useForm<EventsFiltersForm>({
    defaultValues: appliedFilters,
  });

  const { reset, watch } = form;

  const handleSubmit = useCallback(
    (filters: EventsFiltersForm) => {
      setAppliedFilters(filters);

      // Data Layer
      pushEvent({
        event: GoogleAnalyticsEvent.Filter,
        content_group: FilterContentGroup.EventList,
      });
    },
    [setAppliedFilters, pushEvent]
  );

  const { timeInterval } = watch();

  useEffect(() => {
    setAppliedFilters((current) => ({ ...current, timeInterval }));
  }, [setAppliedFilters, timeInterval]);

  const handleReset = useCallback(() => {
    const resetValues = { ...cloneDeep(FORM_DEFAULT_VALUES), timeInterval };
    reset(resetValues);
    handleSubmit(resetValues);
  }, [timeInterval, reset, handleSubmit]);

  const handleSummaryChange = useCallback(
    (filters: EventsFiltersForm) => {
      reset(filters);
      handleSubmit(filters);
    },
    [handleSubmit, reset]
  );

  return (
    <Container containerFrom="xl" pagePaddingX className="pb-20 pt-8">
      <FormProvider {...form}>
        {/* Filters */}
        <EventsFilters
          labels={labels}
          loading={loading}
          onSubmit={handleSubmit}
          timeIntervalEvents={timeIntervalEvents}
        />
        <EventsFiltersSummary
          labels={labels}
          appliedFilters={appliedFilters}
          setAppliedFilters={handleSummaryChange}
          onReset={handleReset}
        />
        {/* Grid */}
        {error ? (
          <div>{labels["events-fetch-error"]}</div>
        ) : (
          <EventsGrid
            labels={labels}
            loading={loading}
            events={filteredEvents}
            eventDetailPagePath={eventDetailPagePath}
          />
        )}
      </FormProvider>
    </Container>
  );
};
